// WPS-021 · Customer Dashboard self-service · Wunli atelier
// Real-data dashboard: cards · orders · Smart Card stats · CRM profile.
// Mobile-first compact · Executive Light theme.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ordersApi, cardsApi, smartCardApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import {
  Plus, CreditCard, Download, Eye, LogOut, Settings,
  Package, Truck, Clock, CheckCircle, ExternalLink, Copy,
  Sparkles, FileText,
} from 'lucide-react';

const STATUS_META = {
  pending:         { label: 'Cart',           color: '#64748b', bg: '#f1f5f9' },
  submitted:       { label: 'In Review',      color: '#a16207', bg: '#fef3c7' },
  pre_production:  { label: 'Pre-Production', color: '#a16207', bg: '#fef3c7' },
  approved:        { label: 'Approved',       color: '#15803d', bg: '#dcfce7' },
  request_changes: { label: 'Changes Needed', color: '#b91c1c', bg: '#fee2e2' },
  in_production:   { label: 'Printing',       color: '#0369a1', bg: '#dbeafe' },
  shipped:         { label: 'Shipped',        color: '#0369a1', bg: '#dbeafe' },
  completed:       { label: 'Delivered',      color: '#15803d', bg: '#dcfce7' },
  cancelled:       { label: 'Cancelled',      color: '#64748b', bg: '#f1f5f9' },
  rejected:        { label: 'Rejected',       color: '#b91c1c', bg: '#fee2e2' },
};

const Section = ({ label, action, children }) => (
  <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
      <div style={{
        fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
        letterSpacing: '0.18em', textTransform: 'uppercase',
        color: '#64748b',
      }}>{label}</div>
      {action}
    </div>
    {children}
  </div>
);

const Stat = ({ icon, label, value, accent = '#01579b' }) => (
  <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', padding: '14px' }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      fontSize: '9px', fontFamily: 'JetBrains Mono, monospace',
      letterSpacing: '0.15em', textTransform: 'uppercase',
      color: '#64748b', marginBottom: '6px',
    }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: '22px', fontWeight: 800, color: accent, letterSpacing: '-0.02em' }}>{value}</div>
  </div>
);

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [cards, setCards] = useState([]);
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem('user_token')) {
      navigate('/login');
      return;
    }
    load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    const r = await ordersApi.myDashboard();
    if (r?.success && r.data) {
      setProfile(r.data.profile);
      setCards(r.data.cards || []);
      setOrders(r.data.orders || []);
      setCounts(r.data.counts || {});
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('user_token');
    localStorage.removeItem('customerAuth');
    navigate('/');
  };

  const copySmartUrl = () => {
    if (!profile?.smartCardSlug) return;
    const url = `https://wunli.com/sc/${profile.smartCardSlug}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'URL copied', description: url });
  };

  const downloadPreview = async (cardId) => {
    const r = await cardsApi.previewPdf(cardId);
    if (r?.success && r.data?.url) {
      window.open(r.data.url, '_blank');
      toast({ title: 'Preview ready', description: 'Watermarked proof · not for printing' });
    } else {
      toast({ title: 'Preview failed', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '12px', color: '#64748b', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          Loading your dashboard…
        </div>
      </div>
    );
  }

  const fullName = profile?.fullName || 'Customer';
  const firstName = fullName.split(' ')[0];
  const smartUrl = profile?.smartCardSlug ? `https://wunli.com/sc/${profile.smartCardSlug}` : null;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', paddingBottom: '40px' }}>
      {/* Header noir */}
      <div style={{ background: '#0a0a0a', color: '#ffffff' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
            <div style={{ width: '32px', height: '32px', background: '#c9a961', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCard className="h-4 w-4" style={{ color: '#0a0a0a' }} />
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', letterSpacing: '0.1em' }}>WUNLI</div>
          </Link>
          <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'transparent', border: '1px solid #334155', color: '#cbd5e1', padding: '6px 12px', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
            <LogOut className="h-3 w-3" /> Logout
          </button>
        </div>
        <div style={{ maxWidth: '960px', margin: '0 auto', padding: '8px 20px 24px' }}>
          <div style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.2em', color: '#c9a961', marginBottom: '8px' }}>
            WUNLI · PRINT STUDIO
          </div>
          <div style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '-0.02em' }}>
            Welcome back, {firstName}
          </div>
          <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
            Atelier-grade cards · Smart Card live · marketing on your terms
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '20px 16px' }}>
        {/* Stat grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          <Stat icon={<CreditCard className="h-3 w-3" />} label="Cards" value={cards.length} />
          <Stat icon={<Package className="h-3 w-3" />} label="Orders" value={counts?.totalOrders ?? 0} />
          <Stat icon={<Clock className="h-3 w-3" />} label="In Process" value={counts?.pendingOrders ?? 0} accent="#a16207" />
          <Stat icon={<Eye className="h-3 w-3" />} label="Smart Views" value={profile?.smartCardViews ?? 0} accent="#c9a961" />
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          <Link to="/create" style={{ textDecoration: 'none' }}>
            <Button style={{ width: '100%', background: '#01579b', color: '#ffffff', padding: '14px', fontSize: '13px', fontWeight: 700 }}>
              <Plus className="h-4 w-4 mr-2" /> New Card
            </Button>
          </Link>
          <Link to="/profile" style={{ textDecoration: 'none' }}>
            <Button variant="outline" style={{ width: '100%', padding: '14px', fontSize: '13px', fontWeight: 700, borderColor: '#0f172a', color: '#0f172a' }}>
              <Settings className="h-4 w-4 mr-2" /> Profile
            </Button>
          </Link>
          {smartUrl && (
            <Button onClick={() => window.open(smartUrl, '_blank')} variant="outline" style={{ padding: '14px', fontSize: '13px', fontWeight: 700, borderColor: '#c9a961', color: '#0a0a0a', background: '#fef3c7' }}>
              <ExternalLink className="h-4 w-4 mr-2" /> View Smart Card
            </Button>
          )}
        </div>

        {/* Smart Card spotlight */}
        {smartUrl && (
          <Section label="Your Smart Card · Live" action={
            <button onClick={copySmartUrl} style={{ background: 'transparent', border: 'none', color: '#01579b', fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Copy className="h-3 w-3" /> Copy URL
            </button>
          }>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px', padding: '14px', background: '#0a0a0a', color: '#c9a961' }}>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', wordBreak: 'break-all', flex: 1, minWidth: '200px' }}>
                {smartUrl}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => window.open(smartUrl, '_blank')} style={{ background: '#c9a961', color: '#0a0a0a', border: 'none', padding: '8px 12px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
                  <ExternalLink className="h-3 w-3 inline mr-1" /> Preview
                </button>
                <button onClick={() => window.open(smartCardApi.vcardUrl(profile.smartCardSlug), '_blank')} style={{ background: 'transparent', color: '#c9a961', border: '1px solid #c9a961', padding: '8px 12px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
                  <Download className="h-3 w-3 inline mr-1" /> vCard
                </button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '12px' }}>
              <Stat icon={<Eye className="h-3 w-3" />} label="Views" value={profile?.smartCardViews ?? 0} accent="#0a0a0a" />
              <Stat icon={<Download className="h-3 w-3" />} label="vCard Saves" value={profile?.vcardDownloads ?? 0} accent="#0a0a0a" />
              <Stat icon={<Sparkles className="h-3 w-3" />} label="Stage" value={(profile?.lifecycleStage || 'lead').replace('_',' ')} accent="#c9a961" />
            </div>
          </Section>
        )}

        {/* My Cards */}
        <Section label={`Your Business Cards · ${cards.length}`} action={
          <Link to="/create" style={{ fontSize: '11px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#01579b', textDecoration: 'none', fontWeight: 700 }}>
            + Add New
          </Link>
        }>
          {cards.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <CreditCard className="h-12 w-12" style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>No cards yet</div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>Create your first atelier-grade card</div>
              <Link to="/create">
                <Button style={{ background: '#01579b', color: '#ffffff' }}>
                  <Plus className="h-4 w-4 mr-2" /> Create your first card
                </Button>
              </Link>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '8px' }}>
              {cards.map(card => (
                <div key={card.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', border: '1px solid #e2e8f0', background: '#ffffff' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{card.companyName || 'Untitled'}</div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                      {card.personalName} · {card.email}
                    </div>
                    <div style={{ fontSize: '9px', fontFamily: 'JetBrains Mono, monospace', color: '#94a3b8', marginTop: '4px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      Tier {card.tier || 'signature'} · {card.orientation || 'horizontal'} · {new Date(card.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button onClick={() => navigate(`/preview/${card.id}`)} title="Preview" style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', color: '#01579b' }}>
                      <Eye className="h-4 w-4" />
                    </button>
                    <button onClick={() => downloadPreview(card.id)} title="Download proof PDF" style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', color: '#01579b' }}>
                      <Download className="h-4 w-4" />
                    </button>
                    <button onClick={() => navigate(`/canvas/${card.id}`)} title="Open canvas editor" style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', color: '#c9a961', fontSize: '14px', fontWeight: 800 }}>
                      ✎
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Order history */}
        <Section label={`Order History · ${orders.length}`}>
          {orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px', fontSize: '12px', color: '#64748b' }}>
              No orders yet. Place your first card order when you are ready.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '6px' }}>
              {orders.map(o => {
                const meta = STATUS_META[o.status] || STATUS_META.pending;
                return (
                  <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #e2e8f0', background: '#ffffff' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                          ${(o.total || 0).toFixed(2)}
                        </span>
                        <span style={{ fontSize: '9px', fontFamily: 'JetBrains Mono, monospace', padding: '2px 8px', background: meta.bg, color: meta.color, letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 700 }}>
                          {meta.label}
                        </span>
                      </div>
                      <div style={{ fontSize: '10px', fontFamily: 'JetBrains Mono, monospace', color: '#94a3b8', marginTop: '4px', letterSpacing: '0.05em' }}>
                        #{(o.id || '').slice(0, 8)} · {new Date(o.createdAt).toLocaleDateString()}
                        {o.trackingNumber && ` · Tracking ${o.trackingNumber}`}
                      </div>
                    </div>
                    {o.status === 'shipped' && o.trackingNumber && (
                      <Truck className="h-4 w-4" style={{ color: '#0369a1' }} />
                    )}
                    {o.status === 'completed' && (
                      <CheckCircle className="h-4 w-4" style={{ color: '#15803d' }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default CustomerDashboard;
